import { useRecoilValue } from "recoil";
import { ActionsCell } from "../../../Component/Common/Components/DataTable/ActionsCell";
import { DefaultCell } from "../../../Component/Common/Components/DataTable/DefaultCell";
import { AllOccupationalLevelList, EmployeeLevelList, provinceList } from "../../../Component/Common/Constants/Lists/DropdownListOptions";
import { PlatformPeopleEditRole, UserAdminRole } from "../../../Component/Common/Constants/UserRoles";
import { PlatformPeopleEmploymentData } from "../../../Component/Model/Dto/Platform/PlatformPeople";
import { ActionType, TableColumn } from "../../../Component/Model/Table/Table";
import { DateFormatter } from "../../../Component/Model/helpers";
import { userRolesState } from "../../../State/user.Atom";

interface EmploymentDataRows {
    period: string,
    jobTitle: string,
    occupationalLevel: string,
    employmentType: string,
    province: string,
    municipality: string,
    siteName: string,
    actions: ActionType[];
}

interface EmploymentDataTable {
    columns: Array<TableColumn>;
    rows: Array<EmploymentDataRows>;
}

export function EmploymentDataTableMapper(dataObject: PlatformPeopleEmploymentData[], handleEdit: (id: number) => void, handleDelete: (id: number, reportingPeriod: string) => void): EmploymentDataTable {
    const editRoleAccess = [
        PlatformPeopleEditRole,
        UserAdminRole
    ]

    const userRoles = useRecoilValue(userRolesState);
    const isEditable = userRoles.some(r => editRoleAccess.includes(r));
    function getActionsColumn(item: PlatformPeopleEmploymentData, index: number) {
        const actions: ActionType[] = [
            isEditable ? {
                key: `assign_${index}`,
                label: 'Edit Record',
                action: () => {
                    handleEdit(item.platformPeopleEmploymentDataId!);
                },
                icon: {
                    color: 'info',
                    name: 'edit'
                }
            } :
                {
                    key: `assign_${index}`,
                    label: 'View Record',
                    action: () => {
                        handleEdit(item.platformPeopleEmploymentDataId!);
                    },
                    icon: {
                        color: 'info',
                        name: 'visibility'
                    }
                },
            isEditable
                ? {
                    key: index.toString(),
                    label: 'Delete',
                    action: () => {
                        handleDelete(item.platformPeopleEmploymentDataId!, getPeriod(item))
                    },
                    icon: {
                        color: 'error',
                        name: 'delete',
                    },
                }
                : null,
        ].filter(Boolean) as ActionType[];

        return actions;
    }

    function getPeriod(item: PlatformPeopleEmploymentData) {
        var endDate = "Current"
        if (item.endDate !== null) {
            endDate = DateFormatter(item.endDate.toString())
        }
        return DateFormatter(item.startDate.toString()) + " - " + endDate;
    }

    const columns = [
        { Header: 'Period', accessor: 'period', id: 'period', Cell: ({ value }: any) => <DefaultCell value={value} /> },
        { Header: 'Job Title', accessor: 'jobTitle', id: 'jobTitle', Cell: ({ value }: any) => <DefaultCell value={value} /> },
        { Header: 'Occupational Level', accessor: 'occupationalLevel', id: 'occupationalLevel', Cell: ({ value }: any) => <DefaultCell value={value} /> },
        { Header: 'Employment Type', accessor: 'employmentType', id: 'employmentType', Cell: ({ value }: any) => <DefaultCell value={value} /> },
        { Header: 'Province', accessor: 'province', id: 'province', Cell: ({ value }: any) => <DefaultCell value={value} /> },
        { Header: 'Municipality', accessor: 'municipality', id: 'municipality', Cell: ({ value }: any) => <DefaultCell value={value} /> },
        { Header: 'Site Name', accessor: 'siteName', id: 'siteName', Cell: ({ value }: any) => <DefaultCell value={value} /> },
        { Header: 'Actions', accessor: 'actions', Cell: ({ value }: any) => <ActionsCell actions={value} /> },
    ];
    if (dataObject != undefined && dataObject.length > 0) {
        const rows = dataObject.map((item: PlatformPeopleEmploymentData, index: number) => ({
            period: getPeriod(item),
            jobTitle: item.jobTitle,
            occupationalLevel: AllOccupationalLevelList.find(x => x.id === item.occupationalLevelId!)?.value!,
            employmentType: EmployeeLevelList.find(x => x.id === item.employmentTypeId!)?.value!,
            province: provinceList.find(x => x.id === item.provinceId!)?.value!,
            municipality: item.municipality!,
            siteName: item.siteName,
            actions: getActionsColumn(item, index),
        }));
        return { columns: columns, rows: rows };
    }
    else {
        return { columns: columns, rows: [] };
    }
}